import Sheet from './Sheet'
import ConfirmDialog from './ConfirmDialog'
import { useState, useEffect } from 'react'
import { useAuth } from '../../store/auth'
import { ZONES, ELEMENTS, RARITY_CONFIG } from '../../types'
import type { ZoneId } from '../../types'

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth >= 768)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)')
    const handler = (e: MediaQueryListEvent) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])
  return isDesktop
}

interface PlayerProfileProps {
  open: boolean
  onClose: () => void
}

const FACTION_COLORS: Record<string, string> = {
  'drifters':      '#5A7A8A',
  'iron-meridian': '#B8EEFF',
  'tidecallers':   '#00F0FF',
  'wrecked':       '#FF4800',
  'sovereign-few': '#00F0FF',
}

const FACTION_LABELS: Record<string, string> = {
  'drifters':      'The Drifters',
  'iron-meridian': 'Iron Meridian',
  'tidecallers':   'Tidecallers',
  'wrecked':       'The Wrecked',
  'sovereign-few': 'Sovereign Few',
}

export default function PlayerProfile({ open, onClose }: PlayerProfileProps) {
  const { player, signOut } = useAuth()
  const [confirmSignOut, setConfirmSignOut] = useState(false)
  const isDesktop = useIsDesktop()

  if (!player) return null

  // All consts below are safe — player is non-null after the guard above
  const zone = ZONES[player.zoneId as ZoneId]
  const el = ELEMENTS[zone.element]
  const factionColor = player.faction ? (FACTION_COLORS[player.faction] ?? '#00F0FF') : '#00F0FF'
  const xpNeeded = (player.level + 1) * 1000
  const xpPct = Math.min((player.xp / xpNeeded) * 100, 100)

  return (
    <>
      <Sheet open={open} onClose={onClose} side={isDesktop ? 'right' : 'bottom'} title="Profile">
        <div className="space-y-6">

          {/* Avatar + name */}
          <div className="flex items-center gap-4">
            <div
              className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 border-2"
              style={{
                background: `radial-gradient(circle, ${factionColor}20, #080E1A)`,
                borderColor: factionColor,
              }}
            >
              <span className="font-display font-black text-2xl text-ab-text">
                {player.username.charAt(0).toUpperCase()}
              </span>
            </div>
            <div>
              <h3 className="font-display font-black text-xl text-ab-text tracking-tight">
                {player.username}
              </h3>
              <span
                className="font-display font-bold text-xs tracking-wide"
                style={{ color: factionColor }}
              >
                {player.faction ? (FACTION_LABELS[player.faction] ?? player.faction) : '—'}
              </span>
              {player.isFoundingMember && (
                <p className="text-xs mt-0.5">
                  <span className="element-pill element-aether text-xs">✨ Founding Member</span>
                </p>
              )}
            </div>
          </div>

          <div className="divider-gold" />

          {/* Level + XP */}
          <div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-label text-xs">Level {player.level}</p>
              <p className="font-display font-bold text-xs text-ab-gold">
                {player.xp} / {xpNeeded} XP
              </p>
            </div>
            <div className="stat-bar">
              <div
                className="stat-bar-fill"
                style={{
                  width: `${xpPct}%`,
                  background: 'linear-gradient(90deg, #FF4800, #00F0FF)',
                }}
              />
            </div>
          </div>

          {/* Currency */}
          <div className="grid grid-cols-2 gap-3">
            <div className="card-gold p-3 text-center">
              <p className="text-label text-xs mb-1">Atlas Coins</p>
              <p className="font-display font-black text-lg text-ab-gold">
                ⚡ {player.atlasCoins.toLocaleString()}
              </p>
            </div>
            <div className="card-gold p-3 text-center">
              <p className="text-label text-xs mb-1">Shards</p>
              <p className="font-display font-black text-lg text-ab-gold">
                ✨ {player.shards.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Zone */}
          <div className="card-base p-4">
            <p className="text-label text-xs mb-2">Current Zone</p>
            <div className="flex items-center gap-3">
              <span className="text-2xl">{el.emoji}</span>
              <div>
                <p className="font-display font-bold text-sm text-ab-text">{zone.name}</p>
                <p className="text-body text-xs opacity-70">{zone.description}</p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-3">
            {player.streakDays > 0 && (
              <div className="rounded-xl border p-3 text-center" style={{ background: 'rgba(255,72,0,0.08)', borderColor: 'rgba(255,72,0,0.3)' }}>
                <p className="text-[10px] text-[#5A5A7A] mb-1 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>Day Streak</p>
                <p className="font-bold text-lg" style={{ color: '#FF4800', fontFamily: 'Rajdhani, system-ui' }}>
                  🔥 {player.streakDays}
                </p>
              </div>
            )}
            <div className="rounded-xl border p-3 text-center" style={{ background: 'rgba(255,255,255,0.03)', borderColor: 'rgba(58,58,90,0.5)' }}>
              <p className="text-[10px] text-[#5A5A7A] mb-1 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>Member Since</p>
              <p className="font-bold text-sm text-[#8A8AA5]" style={{ fontFamily: 'Rajdhani, system-ui' }}>
                {new Date(player.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Rarity legend */}
          <div>
            <p className="text-[10px] text-[#5A5A7A] mb-3 uppercase tracking-wider" style={{ fontFamily: 'Space Mono, monospace' }}>Rarity Tiers</p>
            <div className="flex flex-wrap gap-2">
              {Object.values(RARITY_CONFIG).map(r => (
                <span
                  key={r.label}
                  className="text-[10px] font-bold px-2 py-1 rounded border"
                  style={{ 
                    color: r.color, 
                    background: `${r.color}15`,
                    borderColor: `${r.color}30`,
                    fontFamily: 'Space Mono, monospace',
                  }}
                >
                  {r.label}
                </span>
              ))}
            </div>
          </div>

          <div className="h-px bg-[#3A3A5A]" />

          {/* Sign out */}
          <button
            onClick={() => setConfirmSignOut(true)}
            className="w-full py-2.5 rounded-lg border text-xs font-bold uppercase tracking-wider transition-all hover:opacity-80"
            style={{ 
              color: '#FF4800', 
              borderColor: 'rgba(255,72,0,0.3)',
              background: 'rgba(255,72,0,0.08)',
              fontFamily: 'Space Mono, monospace',
            }}
          >
            Sign Out
          </button>
        </div>
      </Sheet>

      <ConfirmDialog
        open={confirmSignOut}
        title="Sign Out"
        message="Your profile is saved locally. You'll need your PIN to log back in."
        confirmLabel="Sign Out"
        cancelLabel="Stay"
        destructive
        onConfirm={() => { setConfirmSignOut(false); onClose(); signOut(); window.location.href = '/landing'; }}
        onCancel={() => setConfirmSignOut(false)}
      />
    </>
  )
}
